import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskLine } from "@/components/Reveal";
import { TEAM, GOAL, IMAGES } from "@/data/content";


export default function About() {
  return (
    <div data-testid="about-page" className="pt-[72px]">
      {/* Header */}
      <section
        data-testid="about-header"
        className="relative overflow-hidden grain border-b border-white/10"
        style={{
          backgroundImage: `linear-gradient(rgba(10,10,10,0.82), rgba(10,10,10,0.94)), url(${IMAGES.govBuilding})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 md:py-32 relative z-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-blue-400 mb-6">Who we are</p>
          <h1 className="font-heading font-black uppercase tracking-tighter text-5xl sm:text-6xl md:text-7xl leading-[0.95] max-w-4xl">
            <MaskLine delay={0.1}>The people</MaskLine>
            <MaskLine delay={0.22}>
              behind the <span className="text-blue-500">mission.</span>
            </MaskLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="mt-8 max-w-2xl text-lg text-white/60 font-light leading-relaxed"
          >
            A team of engineers, builders, and operators with decades of combined experience in
            government contracting, modular construction, and secure facility delivery.
          </motion.p>
        </div>
      </section>

      {/* Team grid */}
      <section data-testid="team-section" className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 md:py-28">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/10">
          {TEAM.map((m, i) => (
            <Reveal
              key={m.name}
              delay={(i % 3) * 0.08}
              className="group relative p-8 md:p-10 border-r border-b border-white/10 bg-[#141414] hover:bg-[#171717] transition-colors"
              data-testid={`team-card-${i}`}
            >
              <div className="flex items-center justify-end mb-8">
                <ArrowUpRight
                  size={22}
                  className="text-white/20 group-hover:text-blue-500 group-hover:-translate-y-1 group-hover:translate-x-1 transition-all"
                />
              </div>
              <h3 className="font-heading font-bold text-2xl tracking-tight">{m.name}</h3>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-blue-400 mt-2 mb-5">{m.role}</p>
              <p className="text-white/50 text-sm leading-relaxed font-light">{m.bio}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Our Goal */}
      <section
        data-testid="goal-section"
        className="relative bg-[#0A0A0A] border-y border-white/10 py-20 md:py-28"
      >
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-[0.9fr_1.1fr] gap-14 items-center">
          <Reveal>
            <div className="relative overflow-hidden border border-white/10">
              <img
                src={IMAGES.blueprint}
                alt="Engineering precision"
                className="w-full h-[360px] object-cover grayscale"
              />
              <div className="absolute inset-0 bg-blue-600/10 mix-blend-overlay" />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black to-transparent">
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/70">
                  Precision · Compliance · Integrity
                </span>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-blue-400 mb-5">{GOAL.eyebrow}</p>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl md:text-5xl tracking-tight leading-tight mb-8">
              {GOAL.title}
            </h2>
            <p className="text-white/60 text-lg leading-relaxed font-light">{GOAL.body}</p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
